import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileBarChart, Mail, Loader2, AlertTriangle, Trophy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StageBadge } from "@/components/Badges";
import { inrShort, fmtDT } from "@/lib/constants";

const Stat = ({ t, v, variant, testId }) => {
  const cls = { teal: "bg-[#1F9E7A] text-white border-[#1F9E7A]", dark: "bg-[#232323] text-[#F5E04F] border-[#232323]", yellow: "bg-[#F5E04F] text-[#151515] border-[#F5E04F]", orange: "bg-[#F7A934] text-[#151515] border-[#F7A934]" }[variant] || "bg-white border-neutral-200";
  return <div className={`rounded-3xl border p-5 ${cls}`}><p className="text-xs font-medium opacity-80">{t}</p><p data-testid={testId} className="mt-2 font-mono text-3xl font-semibold tabular-nums">{v}</p></div>;
};

const RepCard = ({ r }) => (
  <div data-testid={`report-rep-${r.user_id || "unassigned"}`} className="rounded-3xl border border-neutral-200 bg-white p-5">
    <div className="flex items-center justify-between"><h3 className="font-display text-base font-semibold">{r.name}</h3>
      <span className="rounded-full bg-[#E6F5EF] px-3 py-1 font-mono text-xs text-[#137A5E]">{r.conversions} won · {inrShort(r.won_value)}</span></div>
    <div className="mt-3 grid grid-cols-4 gap-2 text-center">
      {[["Done", r.dispositions.COMPLETED, "bg-[#1F9E7A] text-white"], ["No-show", r.dispositions.NO_SHOW, "bg-[#FFF0D9] text-[#A85E00]"], ["Moved", r.dispositions.RESCHEDULED, "bg-[#FBF5C9] text-[#7A6A00]"], ["Cancel", r.dispositions.CANCELLED, "bg-neutral-100 text-neutral-600"]].map(([t, v, c]) => (
        <div key={t} className={`rounded-2xl py-2 ${c}`}><p className="font-mono text-lg font-semibold">{v}</p><p className="text-[10px]">{t}</p></div>))}
    </div>
    <p className="mt-2 text-xs text-neutral-500">{r.booked} booked this week · show rate {r.show_rate}%</p>
    {r.won_leads.length > 0 && <p className="mt-2 flex items-center gap-1 text-xs text-[#137A5E]"><Trophy className="h-3.5 w-3.5" />{r.won_leads.map((l) => l.full_name).join(", ")}</p>}
    <div className="mt-4">
      <p className="flex items-center gap-1 text-xs font-semibold text-rose-700"><AlertTriangle className="h-3.5 w-3.5" />{r.at_risk_count} at risk · {inrShort(r.at_risk_value)}</p>
      <div className="mt-2 space-y-1.5">
        {r.at_risk.map((l) => (
          <Link key={l.id} to={`/leads/${l.id}`} data-testid={`report-risk-${l.id}`} className="flex items-center justify-between gap-2 rounded-2xl bg-[#F6F8F5] px-3 py-2 hover:bg-neutral-100">
            <div className="min-w-0"><p className="truncate text-sm font-medium">{l.full_name} <span className="text-neutral-400">· {l.company_name || "—"}</span></p><p className="truncate text-[11px] text-rose-600">{l.reasons.join(" · ")}</p></div>
            <StageBadge stage={l.stage} />
          </Link>))}
      </div>
    </div>
  </div>
);

export default function Reports() {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const id = params.get("id");

  const loadList = () => api.get("/reports/weekly").then((r) => { setList(r.data); if (!id && r.data[0]) setParams({ id: r.data[0].id }, { replace: true }); });
  useEffect(() => { loadList(); }, []); // eslint-disable-line
  useEffect(() => { if (id) api.get(`/reports/weekly/${id}`).then((r) => setRep(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);

  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/reports/weekly/generate"); const sent = data.email_status?.filter((s) => s.ok).length || 0; const recipients = data.email_status?.length || 0; toast.success(recipients && sent === 0 ? "Report generated · email is not configured" : recipients ? `Report generated · emailed ${sent}/${recipients} managers` : "Report generated"); await loadList(); setParams({ id: data.id }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const t = rep?.totals;
  const sent = rep?.email_status?.filter((s) => s.ok).length || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold text-[#151515] sm:text-3xl">Weekly Win Report</h1><p className="text-sm text-neutral-500">Emailed to managers every Monday at 9:00 AM IST.</p></div>
        <div className="flex gap-2">
          {list.length > 0 && (
            <Select value={id || ""} onValueChange={(v) => setParams({ id: v })}>
              <SelectTrigger data-testid="report-history-select" className="h-10 w-56 rounded-full border-neutral-300 bg-white"><SelectValue placeholder="Pick week" /></SelectTrigger>
              <SelectContent>{list.map((r) => <SelectItem key={r.id} value={r.id}>{fmtDT(r.generated_at)} · {r.trigger.startsWith("MANUAL") ? "manual" : "Monday"}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <button data-testid="report-generate-btn" disabled={busy} onClick={generate} className="flex h-10 items-center gap-1.5 rounded-full bg-[#1F9E7A] px-4 text-sm font-semibold text-white disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileBarChart className="h-4 w-4" />}Generate now
          </button>
        </div>
      </div>
      {!rep && <div data-testid="report-empty" className="rounded-3xl border border-dashed border-neutral-300 p-10 text-center text-neutral-500">No reports yet — the first one goes out next Monday, or tap “Generate now”.</div>}
      {rep && (
        <>
          <p className="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
            <span className="rounded-full border border-neutral-300 px-3 py-1 font-mono">{rep.period_start.slice(0, 10)} → {rep.period_end.slice(0, 10)}</span>
            <span data-testid="report-email-status" className="flex items-center gap-1 rounded-full bg-white px-3 py-1"><Mail className="h-3.5 w-3.5" />{rep.email_status?.length ? `Emailed ${sent}/${rep.email_status.length} managers` : "Email sending…"}</span>
          </p>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat t="Meetings with outcome" v={t.dispositioned} variant="teal" testId="report-total-dispositioned" />
            <Stat t="Conversions" v={t.conversions} variant="dark" testId="report-total-conversions" />
            <Stat t="Won value" v={inrShort(t.won_value)} variant="yellow" testId="report-total-won-value" />
            <Stat t="Leads at risk" v={t.at_risk_count} variant="orange" testId="report-total-at-risk" />
          </div>
          {t.pending_dispositions > 0 && <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{t.pending_dispositions} meetings still have no outcome logged.</p>}
          <div className="grid gap-4 lg:grid-cols-2">{rep.reps.map((r) => <RepCard key={r.user_id || "u"} r={r} />)}</div>
        </>
      )}
    </div>
  );
}
